import {
  Icon,
  InnerHtml,
  theme,
  Tooltip,
  useGlimmerTheme,
} from '@innovamat/glimmer-components';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import type {
  CardStatus,
  StudentsAttempted,
  StudentsFailed,
} from '../../modules/fluency/deck-report';
import { CARD_STATUS } from '../../modules/fluency/deck-report';

export const CELL_VARIANT = {
  CELL_NORMAL_SIZE: 'cell-normal-size',
  CELL_SMALL_SIZE: 'cell-small-size',
} as const;

type CellVariant = (typeof CELL_VARIANT)[keyof typeof CELL_VARIANT];

type Props = {
  status: CardStatus;
  variant: CellVariant;
  studentsFailed?: StudentsFailed[];
  studentsAttempted?: StudentsAttempted[];
  allCellsLocked?: boolean;
  operation?: string;
  noStudentsConnected?: boolean;
};

const StyledCell = styled.div<{
  variant: CellVariant;
  bgColor: string;
  allCellsLocked?: boolean;
  isSkeleton?: boolean;
}>(({ variant, bgColor, allCellsLocked, isSkeleton = false }) => {
  const cellSize = {
    [CELL_VARIANT.CELL_NORMAL_SIZE]: 24,
    [CELL_VARIANT.CELL_SMALL_SIZE]: 16,
  };

  const cellBorderRadius = {
    [CELL_VARIANT.CELL_NORMAL_SIZE]: 3,
    [CELL_VARIANT.CELL_SMALL_SIZE]: 2,
  };

  return css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: ${cellSize[variant]}px;
    height: ${cellSize[variant]}px;
    border-radius: ${cellBorderRadius[variant]}px;

    ${variant === CELL_VARIANT.CELL_NORMAL_SIZE &&
    !isSkeleton &&
    css`
      :hover {
        filter: brightness(90%);
      }
    `}

    background-color: ${allCellsLocked &&
    variant === CELL_VARIANT.CELL_SMALL_SIZE
      ? theme.tokens.color.specific.reports['progress-bar'].empty.value
      : bgColor};
  `;
});

const StyledTooltip = styled(Tooltip)`
  text-align: center;
`;

function CellFluency({
  status,
  variant = CELL_VARIANT.CELL_NORMAL_SIZE,
  studentsFailed,
  studentsAttempted,
  allCellsLocked,
  noStudentsConnected,
  operation,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const theme = useGlimmerTheme();

  const baseTokenReport = theme.tokens.color.specific.reports;
  const getBackgroundColor = (status: CardStatus): string => {
    switch (status) {
      case CARD_STATUS.LOCKED:
      case CARD_STATUS.NOT_SEEN:
        return baseTokenReport['progress-bar'].empty.value;
      case CARD_STATUS.LOW_PARTICIPATION:
        return baseTokenReport.semantic['no-data'].value;
      case CARD_STATUS.MOSTLY_FAILED:
        return baseTokenReport.semantic['low'].value;
      case CARD_STATUS.FAIRLY_FAILED:
        return baseTokenReport.semantic['medium'].value;
      case CARD_STATUS.IN_PROGRESS:
        return baseTokenReport.semantic['high'].value;
      case CARD_STATUS.MEMORIZED:
        return baseTokenReport.semantic['super-high'].value;
      case CARD_STATUS.SKELETON:
        return baseTokenReport.tables.cell['bg-empty'].value;
      default:
        return baseTokenReport.semantic['no-data'].value;
    }
  };

  const backgroundColor = getBackgroundColor(status);

  const renderTooltipMessage = (operation: string | undefined): string => {
    const operationLine = operation
      ? `<strong>${operation}</strong><br />`
      : '';

    if (status === CARD_STATUS.LOCKED) {
      return `${operationLine}${t('fluency.reports.hover.lockedGroup')}`;
    }

    const students =
      status === CARD_STATUS.LOW_PARTICIPATION
        ? studentsAttempted
            ?.map((student) => `${student.firstName} ${student.lastName}`)
            .join('<br />')
        : studentsFailed
            ?.map((student) => `${student.firstName} ${student.lastName}`)
            .join('<br />');

    return `${operationLine}${students || ''}`;
  };

  const shouldDisableTooltip = (): boolean => {
    if (allCellsLocked || noStudentsConnected) {
      return true;
    }

    if (status === CARD_STATUS.LOW_PARTICIPATION) {
      return !studentsAttempted;
    }

    return !studentsFailed;
  };

  return (
    <StyledTooltip
      content={<InnerHtml text={renderTooltipMessage(operation)} />}
      disabled={shouldDisableTooltip()}
      maxWidth={250}
    >
      <StyledCell
        variant={variant}
        bgColor={backgroundColor}
        allCellsLocked={allCellsLocked}
        isSkeleton={status === CARD_STATUS.SKELETON}
      >
        {status === CARD_STATUS.LOCKED && (
          <Icon
            icon="LockedIcon"
            iconColor={theme.tokens.color.alias.cm.icon['icon-disabled'].value}
            size="M"
          />
        )}
      </StyledCell>
    </StyledTooltip>
  );
}

export default CellFluency;
